<template>
  <v-dialog
    :value="dialog"
    :max-width="maxWidth"
    class="chronology-editor"
    scrollable
  >
    <v-card>
      <v-card-title class="body-2 pa-2">
        クロノロジー登録
      </v-card-title>
      <v-card-text class="px-3 py-1" ref="chronologyContent">
        <div class="d-flex">
          <v-select
            class="mr-2"
            :items="categories"
            label="カテゴリ"
            style="font-size: 14px; max-width: 225px"
            v-model="target.category"
          />
          <v-select
            class="flex-grow-1 ml-2"
            :items="sections"
            label="情報種別"
            style="font-size: 14px"
            v-model="target.section"
          />
        </div>

        <div class="d-flex">
          <v-text-field
            class="flex-grow-1 mr-2"
            style="font-size: 14px"
            label="入力者"
            v-model="target.user"
          ></v-text-field>
          <v-card
            height="52"
            outlined
            class="flex-grow-1 ml-2 chronology-datetime-content"
            style="border-color: #757575; max-width: 225px"
          >
            <div class="caption pl-2 pt-1">発生/確認日時</div>
            <datetime
              v-model="target.date"
              :max-datetime="currentDate"
              input-class="chronology-datetime-picker"
              type="datetime"
            >
              <template slot="button-confirm" slot-scope="scope">
                <span v-if="scope.step === 'date'">
                  <v-btn text color="indigo">時刻選択</v-btn>
                </span>
                <span v-if="scope.step === 'time'">
                  <v-btn text color="indigo">OK</v-btn>
                </span>
              </template>
            </datetime>
          </v-card>
        </div>

        <div class="d-flex flex-wrap mb-3">
          <v-select
            class="mr-3 pb-2"
            :items="statuses"
            label="対応状況"
            style="font-size: 14px; max-width: 110px"
            v-model="target.status"
            hide-details
          />
          <v-select
            class="mr-3 pb-2"
            :items="icons"
            label="アイコン"
            style="font-size: 14px; max-width: 100px"
            v-model="target.icon"
            hide-details
          >
            <template v-slot:selection="{ item }">
              <v-icon>{{ item }}</v-icon>
            </template>
            <template v-slot:item="{ parent, item }">
              <v-icon v-if="parent">{{ item }}</v-icon>
            </template>
          </v-select>
          <div style="min-width: 200px">
            <v-color-picker
              v-model="target.color"
              class="no-input"
              hide-canvas
              hide-mode-switch
              mode="hexa"
            ></v-color-picker>
          </div>
        </div>

        <v-text-field
          outlined
          label="件名"
          class="mb-3"
          style="font-size: 14px"
          v-model="target.notes"
          hide-details
          dense
        />

        <v-textarea
          outlined
          label="対応状況"
          class="mb-3"
          v-model="progress"
          rows="3"
          hide-details
          dense
        />

        <div class="d-flex my-2">
          <v-btn x-small outlined color="primary" @click="moveMapCenter()">
            マーカーを配置
          </v-btn>
          <v-btn
            x-small
            outlined
            color="primary"
            @click="moveDevCenter()"
            v-if="isDevelopment"
            class="ml-2"
          >
            中央
          </v-btn>
          <v-spacer />
          <div class="caption" v-if="target.lat && target.lng">
            {{ target.lat.toFixed(5) }}, {{ target.lng.toFixed(5) }}
          </div>
        </div>

        <v-card outlined class="mb-2">
          <LMap
            ref="chronologyMap"
            id="chronologyMap"
            :zoom="11"
            :center="center"
            :options="{ zoomControl: false, scrollWheelZoom: false }"
            :zoom-animation="false"
            style="height: 200px; width: 100%"
          >
            <LTileLayer
              url="https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png"
            />
            <LControl position="topright" v-if="leafletMap">
              <ZoomControl :map="leafletMap" />
            </LControl>
            <LMarker
              v-if="target.lat && target.lng"
              :lat-lng="[target.lat, target.lng]"
              draggable
              @dragend="dragend"
            />
          </LMap>
        </v-card>

        <v-card class="pa-3" min-height="210" outlined>
          <v-file-input
            v-model="currentFile"
            accept=".jpg,.jpeg,.png,.pdf"
            ref="inputFile"
            style="display: none"
            @change="loadFile()"
          />
          <div class="d-flex mb-1">
            <div class="caption mr-4">添付ファイル</div>
            <v-btn @click="selectFile()" x-small>ファイルを追加する</v-btn>
          </div>
          <v-card class="d-flex flex-wrap" flat>
            <v-card
              v-for="(row, index) in files"
              :key="index"
              class="mr-2 mt-2 pa-2"
              outlined
            >
              <div style="cursor: pointer" @click="preview(row)">
                <template v-if="isPdf(row)">
                  <v-card
                    width="120"
                    height="100"
                    flat
                    class="d-flex justify-center align-center"
                  >
                    <v-icon x-large>mdi-file-pdf-box</v-icon>
                  </v-card>
                </template>
                <template v-else-if="row.objectUrl">
                  <v-img
                    :src="row.objectUrl"
                    width="120"
                    height="100"
                    contain
                    class="d-flex"
                  />
                </template>
                <template v-else-if="row.url">
                  <v-img
                    :src="row.url"
                    width="120"
                    height="100"
                    contain
                    class="d-flex"
                  />
                </template>
              </div>
              <div class="d-flex pt-1">
                <v-btn x-small @click="removeFile(index)">
                  削除
                </v-btn>
              </div>
            </v-card>
          </v-card>
        </v-card>
      </v-card-text>
      <v-card-actions class="px-3">
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="$emit('close')">
          キャンセル
        </v-btn>
        <v-btn color="primary" @click="save()">
          保存
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import moment from "moment";
import { LMap, LTileLayer, LMarker, LControl } from "vue2-leaflet";
import { BRANCH_OFFICE } from "../../enums/BranchOffice";
import { Chronology } from "../../enums/Chronology";
import ZoomControl from "../base-map/layers/ZoomControl";
import chronologyAPI from "../../utils/chronologyAPI";
export default {
  name: "ChronologyRegister",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LControl,
    ZoomControl
  },

  props: {
    dialog: Boolean,
    maxWidth: {
      type: [String, Number],
      default: 500
    }
  },

  data() {
    return {
      target: {},
      center: [BRANCH_OFFICE[0].lat, BRANCH_OFFICE[0].lng],
      currentLocation: [BRANCH_OFFICE[0].lat, BRANCH_OFFICE[0].lng],
      leafletMap: null,
      currentDate: moment().toISOString(),
      progress: "",
      currentFile: undefined,
      files: []
    };
  },

  computed: {
    categories() {
      return Chronology.categories;
    },
    sections() {
      return Chronology.sections;
    },
    statuses() {
      return Chronology.statuses;
    },
    icons() {
      return Chronology.icons;
    },
    userId() {
      return this.$store.getters.user.id;
    },
    isDevelopment() {
      return process.env.NODE_ENV === "development";
    }
  },

  watch: {
    dialog(val) {
      if (!val) return;
      this.load();
    }
  },

  created() {
    this.getCurrentLocation();
  },

  methods: {
    getCurrentLocation() {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.currentLocation = [
            position.coords.latitude,
            position.coords.longitude
          ];
        },
        error => {
          console.log(error);
        }
      );
    },

    load() {
      const user = this.$store.getters.user;
      this.target = {
        id: 0,
        category: this.categories[0],
        section: this.sections[0],
        date: moment().toISOString(),
        user: user["custom:department"] + " " + user["custom:user_name"],
        notes: "",
        lat: this.currentLocation[0],
        lng: this.currentLocation[1]
      };
      this.progress = "";

      if (!this.target.lat) {
        this.target.lat = BRANCH_OFFICE[0].lat;
      }
      if (!this.target.lng) {
        this.target.lng = BRANCH_OFFICE[0].lng;
      }
      if (!this.target.status) {
        this.target.status = this.statuses[0];
      }
      if (!this.target.icon) {
        this.target.icon = this.icons[0];
      }
      if (!this.target.color) {
        this.target.color = "#F44336B1";
      }
      setTimeout(() => {
        this.leafletMap = this.$refs.chronologyMap.mapObject;
        this.leafletMap.invalidateSize();
        this.center = [this.target.lat, this.target.lng];
        this.leafletMap.setView(this.center, 14);

        const container = this.$refs.chronologyContent;
        container.scrollTop = 0;
      }, 500);
    },

    async saveChronology() {
      const d = moment.utc(this.target.date);
      const params = {
        ...this.target,
        userId: this.userId,
        date: Number(d.format("YYYYMMDDHHmm"))
      };
      if (params.s3_url !== undefined) {
        delete params.s3_url;
      }
      const response = await chronologyAPI.post("/chronology", params);
      return response && response.success === true ? response : null;
    },

    async save() {
      const res = await this.saveChronology();
      if (res === null) {
        alert("保存に失敗しました");
        return;
      }

      const chronologyId = res.id;
      const hash = `${res.hash}-${chronologyId}`;

      // 対応状況画像登録
      const progressFiles = [];
      if (this.files.length > 0) {
        const processes = this.files.map(row => {
          return (async () => {
            if (row.file) {
              const s3Key = await chronologyAPI.uploadFile(row.file);
              if (s3Key) {
                progressFiles.push(s3Key);
              }
            }
          })();
        });
        await Promise.all(processes);
      }

      // 対応状況データ登録
      const progressParams = {
        hash: hash,
        id: 0,
        chronologyId: chronologyId,
        date: Number(moment.utc(this.target.date).format("YYYYMMDDHHmm")),
        user: this.target.user,
        userId: this.userId,
        progress: this.progress,
        files: progressFiles
      };
      const response = await chronologyAPI.post("progress", progressParams);
      if (response && response.success === true) {
        this.$emit("close");
      }
    },

    dragend(e) {
      const latlng = e.target.getLatLng();
      this.target.lat = latlng.lat;
      this.target.lng = latlng.lng;
    },

    moveMapCenter() {
      const latlng = this.leafletMap.getCenter();
      this.target.lat = latlng.lat;
      this.target.lng = latlng.lng;
    },

    moveDevCenter() {
      this.leafletMap.panTo([BRANCH_OFFICE[0].lat, BRANCH_OFFICE[0].lng]);
      this.target.lat = BRANCH_OFFICE[0].lat;
      this.target.lng = BRANCH_OFFICE[0].lng;
    },

    isPdf(row) {
      if (!row) return false;
      if (row.file) {
        return row.file.type === "application/pdf";
      } else {
        return row.url.endsWith(".pdf");
      }
    },

    preview(row) {
      if (!row) return;
      if (row.file && row.objectUrl) {
        window.open(row.objectUrl);
      } else if (row.url) {
        window.open(row.url);
      }
    },

    removeFile(index) {
      if (!confirm("削除しますか？") || this.files.length <= index) {
        return;
      }
      if (index < 0) return;
      this.files.splice(index, 1);
    },

    selectFile() {
      const elm = this.$refs.inputFile;
      elm.$children[0].$el.click();
    },

    async loadFile() {
      if (!this.currentFile) return;
      if (this.currentFile.size > Chronology.MAX_FILE_SIZE) {
        this.currentFile = null;
        alert("ファイルサイズが10MBを超えています");
        return;
      }
      this.files.push({
        file: this.currentFile,
        objectUrl: URL.createObjectURL(this.currentFile)
      });
      this.currentFile = undefined;
    }
  }
};
</script>

<style lang="scss">
.chronology-datetime-content {
  .chronology-datetime-picker {
    cursor: pointer;
    background-color: white;
    color: black;
    font-size: 12px;
    padding: 0;
    margin: 0;
    height: 22px;
    @media screen and (min-width: 900px) {
      font-size: 16px;
    }
  }
  .vdatetime-popup {
    z-index: 6002 !important;
  }
}
</style>

<style lang="scss">
.no-input {
  .v-color-picker__controls {
    padding: 12px;
    .v-color-picker__preview {
      .v-color-picker__sliders {
        .v-color-picker__hue {
          margin-bottom: 14px;
        }
      }
    }
    .v-color-picker__edit {
      margin: 0;
      .v-color-picker__input:last-child {
        display: none;
      }
    }
  }
}
</style>
